import { useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Home, Pencil, Save, X, Loader2 } from "lucide-react";
import { LeadSourcePill } from "./LeadSource";

export function QuoteContactCard({ quoteId, data, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const start = () => {
    setForm({ name: data.name || "", email: data.email || "", phone: data.phone || "",
      postcode: data.postcode || "", address: data.address || "" });
    setEditing(true);
  };
  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/admin/quote/${encodeURIComponent(quoteId)}/contact`, form);
      toast.success("Contact details updated");
      onSaved && onSaved(r.data);
      setEditing(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update contact details"); }
    setSaving(false);
  };

  const FIELDS = [
    ["name", "Full name", "text", "Client name"],
    ["email", "Email", "email", "client@example.com"],
    ["phone", "Phone", "tel", "07…"],
    ["postcode", "Postcode", "text", "GU11 3JP"],
    ["address", "Address", "text", "Site address"],
  ];

  return (
    <div className="border border-[#E5E7EB] bg-white p-4" data-testid="quote-contact-card">
      <div className="flex items-center justify-between mb-2">
        <div className="label-eyebrow">Contact</div>
        {!editing && (
          <button data-testid="quote-contact-edit" onClick={start}
            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-[#C79A3B] hover:text-[#A87E28]">
            <Pencil size={11} /> Edit
          </button>
        )}
      </div>
      {editing ? (
        <div data-testid="quote-contact-form">
          {FIELDS.map(([k, label, type, placeholder]) => (
            <div className="mb-2" key={k}>
              <label className="text-[10px] uppercase tracking-widest text-[#6B7280] font-bold block mb-0.5">{label}</label>
              <input data-testid={`quote-contact-${k}`} type={type} value={form[k] || ""} placeholder={placeholder}
                onChange={(e) => setForm(f => ({ ...f, [k]: e.target.value }))}
                className="w-full border border-[#E5E7EB] px-2.5 py-1.5 text-sm focus:border-[#C79A3B] outline-none" />
            </div>
          ))}
          <div className="flex gap-2 mt-3">
            <button data-testid="quote-contact-save" onClick={save} disabled={saving}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold bg-[#12263A] text-white hover:bg-[#0C1B2B] disabled:opacity-50">
              {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save
            </button>
            <button data-testid="quote-contact-cancel" onClick={() => setEditing(false)} disabled={saving}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold border border-[#E5E7EB] text-[#6B7280] hover:border-[#12263A] disabled:opacity-50">
              <X size={12} /> Cancel
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="font-bold text-[#12263A]" data-testid="quote-contact-name">{data.name || "—"}</div>
          {data.email
            ? <div className="text-xs text-[#6B7280] mt-1 flex items-center gap-1.5"><Mail size={12} /> <a className="de-link" href={`mailto:${data.email}`}>{data.email}</a></div>
            : <div className="text-xs text-[#C0392B] font-bold mt-1 flex items-center gap-1.5" data-testid="quote-contact-no-email"><Mail size={12} /> No email on file — estimates can&apos;t be sent until one is added</div>}
          {data.phone && <div className="text-xs text-[#6B7280] mt-1 flex items-center gap-1.5"><Phone size={12} /> <a className="de-link" href={`tel:${data.phone}`}>{data.phone}</a></div>}
          {data.postcode && <div className="text-xs text-[#6B7280] mt-1 flex items-center gap-1.5"><MapPin size={12} /> {data.postcode}</div>}
          {data.address && <div className="text-xs text-[#6B7280] mt-1 flex items-center gap-1.5"><Home size={12} /> {data.address}</div>}
          {data.lead_source?.channel && (
            <div className="mt-2 flex items-center gap-1.5" data-testid="quote-contact-lead-source">
              <span className="text-[10px] uppercase tracking-widest text-[#6B7280] font-bold">Came from</span>
              <LeadSourcePill source={data.lead_source} rowKey="detail" />
            </div>
          )}
        </>
      )}
    </div>
  );
}
